import { CellModel, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import rp, { AppHost, GridEditorAdapter, GridEditorOptions, Service, RequestParamsOrder, RowData } from "@reporting/rp-app-host";
import { File } from "@reporting/rp-types/etl";
import { msg } from "@omegagrid/localize";
import { delimiters, quotes } from "../types";
import { downloadEtlFile, uploadEtlFile } from "./fileActions";

const PAGE_SIZE = 100;
const DEFAULT_FIELDS = ['id_file', 'input_file', 'description', 'type', 'enabled', 'sync', 'delimiter', 'enclosure', 'header', 'date_created', 'who_created_dv', 'date_uploaded'];
const DEFAULT_ORDER: RequestParamsOrder = [['input_file', 'asc']];

export class FilesGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	constructor(
		private host: AppHost,
		private etlClient: Service
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 5,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: 'red'
			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: 'yellow'
			}, {
				action: 'open',
				title: msg('Open file'),
				icon: 'book-open',
				color: 'blue'
			}, {
				action: 'download',
				title: msg('Download'),
				icon: 'download',
				color: 'gray'
			}, {
				action: 'upload',
				title: msg('Upload'),
				icon: 'upload',
				color: 'green'
			}],
			filter: false
		}, {
			data: 'id_file',
			header: '#',
			t: 'n',
			z: '0',
			e: false,
			w: 80
		}, {
			data: 'input_file',
			header: msg('File'),
			t: 't',
			e: true,
			w: 200
		}, {
			data: 'description',
			header: msg('Description'),
			t: 't',
			e: true,
			w: 200
		}, {
			data: 'sync',
			header: msg('Sync'),
			t: 'b',
			e: true,
			w: 80
		}, {
			data: 'delimiter',
			header: msg('CSV delimiter'),
			t: 's',
			e: true,
			w: 120,
			content: delimiters.map(([k, v]) => ({k, v}))
		}, {
			data: 'enclosure',
			header: msg('CSV quote'),
			t: 's',
			e: true,
			w: 120,
			content: quotes.map(([k, v]) => ({k, v}))
		}, {
			data: 'header',
			header: msg('CSV header'),
			t: 'b',
			e: true,
			w: 120
		}, {
			data: 'who_created_dv',
			header: msg('Created by'),
			t: 't',
			e: false,
			w: 180
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			e: false,
			w: 145
		}, {
			data: 'date_uploaded',
			header: msg('Uploaded'),
			t: 'dt',
			e: false,
			w: 145
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]>{
		const files = await this.etlClient.get<File[]>(`files`, this.createRequestParams(filters, {
			fields: DEFAULT_FIELDS,
			order: DEFAULT_ORDER,
			count: PAGE_SIZE,
		}));

		return files.map(file => ({data: file}));
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			rowMenu: {
				addRows: true,
				deleteRows: true,
			},
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index}, row.data));
		const result = await this.etlClient.post(`files`, data);
	}

	async onAction(action: string, cell: CellModel) {
		switch (action) {
			case 'open':
				this.host.open(`etl-file-${cell.row.data['id_file']}`, {title: cell.row.data['input_file']});
				break;
			case 'download':
				rp.host.showAlert({ type: 'info', title: msg('Export'), content: msg('Export started.') })
				await downloadEtlFile(this.etlClient, cell.row.data as File);
				break;
			case 'upload':
				if (await uploadEtlFile(this.etlClient, cell.row.data['id_file'])) {
					await this.editor.refresh();
					rp.host.showAlert({ type: 'success', title: msg('Upload'), content: msg('Completed successfully.') })
				}
				break;
		}
	}

}
